import { type ReactNode } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router';

import { useAuth } from '@/shared/auth/AuthContext';

interface PublicOnlyProps {
  children: ReactNode;
}

function getSafeReturnTo(raw: string | null): string {
  if (!raw) return '/';
  let decoded: string;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    return '/';
  }
  // Whitelist: must start with single `/` and not be a protocol-relative URL.
  if (!decoded.startsWith('/') || decoded.startsWith('//')) {
    return '/';
  }
  return decoded;
}

export function PublicOnly({ children }: PublicOnlyProps): JSX.Element {
  const { status } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  if (status === 'authenticated') {
    const returnTo = getSafeReturnTo(searchParams.get('returnTo'));
    return <Navigate to={returnTo} replace />;
  }

  if (status === 'mfa_challenge' && location.pathname !== '/mfa') {
    return <Navigate to="/mfa" replace />;
  }

  if (status === 'mfa_enrollment' && location.pathname !== '/mfa/setup') {
    return <Navigate to="/mfa/setup" replace />;
  }

  if (status === 'manager_tenant_select' && location.pathname !== '/manager/select-tenant') {
    return <Navigate to="/manager/select-tenant" replace />;
  }

  return <>{children}</>;
}
